<template lang="pug">
  div(class="m-document-saved-participants-routes")
    div(class="m-document-saved-participants-routes__content")
      h2(class="title-32px fw-700 mb-2") {{ title }}
      p(
        v-if="!isEdit"
        class="text-14px fw-300"
      ) {{ $t('Create route for quick document sending') }}
      v-form
        ValidationObserver(
          ref="form"
          slim
        )
          ValidationProvider(
            :name="$t('Route name')"
            vid="name"
            rules="required"
            mode="lazy"
            v-slot="{ errors }"
          )
            div(class="text-14px dark mb-3") {{ $t('Route name') }}
            v-text-field(
              v-model="form.name"
              :placeholder="$t('Example: Approving documents')"
              filled
              :error-messages="errors"
            )
          div(class="d-flex align-center justify-space-between mb-3")
            div(class="text-14px dark") {{ $t('Recipients') }}
            div(
              v-if="canEditParticipantsRouteStrictness(form.members)"
              class="d-flex align-center"
            )
              v-tooltip(bottom)
                template(v-slot:activator="{ on }")
                  div(
                    v-on="on"
                    class="d-flex align-center"
                  )
                    span(class="text-14px dark fw-300") {{ $t('At least one participant should approve') }}
                    v-switch(
                      v-model="canSkipApprovers"
                      hide-details
                      class="mt-0 pt-0 ml-3"
                    )
                span {{ $t('Document will be approved after first approve') }}
          draggable(
            v-model="form.members"
            handle=".draggable"
            v-bind="dragOptions"
            :draggable="'.draggable-enabled'"
            @start="drag = true"
            @end="onFlipParticipantsEnd"
          )
            transition-group(
              type="transition"
              :name="!drag ? 'flip-list' : null"
            )
              div(
                v-for="(item, idx) in form.members"
                :key="item.localId"
                class="recipient draggable-enabled mb-4"
                :class="{ deleteable: form.members.length > 1 }"
              )
                div(class="recipient__dots draggable text-14px dark") {{ item.priority + 1 }}
                ValidationProvider(
                  tag="div"
                  v-slot="{ errors }"
                  :vid="'emailOrEdrpou_' + idx"
                  :name="wrapQuotesProviderName($t('Recipient`s email or EDRPOU'))"
                  mode="lazy"
                  :debounce="500"
                  :rules="setIdentifierRules(item, idx)"
                  class="recipient__email-field"
                )
                  v-combobox(
                    v-model.trim="item.identifier"
                    :items="item.searchResults"
                    :search-input.sync="item.searchQuery"
                    filled
                    :label="$t('Recipient`s email or EDRPOU')"
                    data-test="document-form-participants-email-input"
                    hide-details="auto"
                    no-filter
                    :error-messages="errors"
                    clearable
                    @update:search-input="handleSearchInput($event, item)"
                    item-text="display"
                    item-value="value"
                    return-object
                    :loading="item.loading"
                    @input="onComboboxInput(item)"
                    :class="{ 'recipient-combobox': !item.searchResults.length }"
                  )
                    template(#item="{ item }")
                      v-list-item-content
                        v-list-item-title {{ item.display }}
                        v-list-item-subtitle(v-if="item.email && item.edrpou") Email: {{ item.email }} | ЄДРПОУ: {{ item.edrpou }}
                        v-list-item-subtitle(v-else-if="item.email") Email: {{ item.email }}
                        v-list-item-subtitle(v-else-if="item.edrpou") ЄДРПОУ: {{ item.edrpou }}
                ValidationProvider(
                  tag="div"
                  v-slot="{ errors }"
                  :vid="'action_' + idx"
                  :name="wrapQuotesProviderName($t('What have to do'))"
                  mode="lazy"
                  rules="required"
                )
                  v-select(
                    :value="item.action"
                    @change="onActionChange($event, item)"
                    filled
                    :label="$t('What have to do')"
                    data-test="document-form-participants-action-select"
                    hide-details="auto"
                    :error-messages="errors"
                    :menu-props="{ bottom: true, offsetY: true }"
                    :items="proParticipantActions"
                  )
                v-btn(
                  v-if="form.members.length > 1"
                  icon
                  class="secondary-button error-btn recipient__remove-btn"
                  data-test="document-form-participants-remove-participant-btn"
                  @click="removeParticipant(idx)"
                )
                  e-svg-icon danger-trash
        div(class="add-more-block")
          v-btn(
            id="addParticipantBtn"
            class="secondary-button xs text-btn"
            data-test="document-form-participants-add-participant-btn"
            @click="addParticipant"
          ) {{ $t('Add recipient') }}
    div(class="m-document-saved-participants-routes__footer")
      v-btn(
        height="48"
        @click="closeModal"
      ) {{ $t('Cancel') }}
      v-btn(
        class="main-button"
        :loading="loading"
        @click="submit"
      ) {{ $t('Save route') }}
</template>

<script>
import Draggable from 'vuedraggable'
import { v4 as uuidv4 } from 'uuid'
import validate from '~/mixins/validation/validate'
import Contact from '~/modules/contacts/models/Contact'
import participants from '~/modules/documents/mixins/participants'
import successSnackbar from '~/modules/snackbar/mixins/successSnackbar'

export default {
  name: 'MDocumentSavedParticipantsRoute',
  components: {
    Draggable
  },
  mixins: [validate, participants, successSnackbar],
  props: {
    route: {
      type: Object,
      default: null
    },
    closeModal: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      form: {
        name: null,
        members: []
      },
      drag: false,
      searchDebounce: null,
      loading: false,
      canSkipApprovers: false
    }
  },
  computed: {
    isEdit() {
      return this.route
    },
    title() {
      return this.isEdit ? this.$t('Edit route') : this.$t('New route')
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'recipient--active'
      }
    }
  },
  created() {
    this.searchDebounce = this._.debounce(item => this.doSearch(item), 300)
    if (this.isEdit) {
      this.setParticipants()
    } else {
      this.addParticipant()
    }
  },
  methods: {
    setParticipants() {
      this.form.name = this.route.name
      this.canSkipApprovers = !this.route.strict
      const members = this._.get(this.route, 'members', [])
      if (this._.get(members, 'length', 0)) {
        members.forEach((member, idx) => {
          this.addParticipant({
            identifier: member.identifier,
            action: member.action,
            priority: idx + 1
          })
        })
      } else {
        // add participant if members array is empty
        this.addParticipant()
      }
    },
    addParticipant(data) {
      const participant = {
        localId: uuidv4(),
        identifier: null,
        action: null,
        priority: this.form.members.length,
        searchResults: [],
        searchQuery: null,
        loading: false
      }
      const resultParticipant = {
        ...participant,
        ...data
      }
      this.form.members.push(resultParticipant)
    },
    removeParticipant(index) {
      this.form.members.splice(index, 1)
      this.$nextTick(() => {
        this.$refs.form.validate()
      })
    },
    onActionChange(value, participant) {
      participant.action = value
    },
    setIdentifierRules(participant, idx) {
      return {
        required: true,
        unique_array_value: {
          array: this.form.members,
          uniqueKey: 'searchQuery',
          index: idx
        },
        not_one_from_list: { array: [this.$Organization.edrpou, this.$User.email] }
      }
    },
    onComboboxInput(item) {
      if (item.identifier && typeof item.identifier === 'object') {
        item.email = item.identifier.email || null
        item.edrpou = item.identifier.edrpou || null
        item.identifier = item.identifier.edrpou || item.identifier.email
      }
    },
    handleSearchInput(val, item) {
      item.searchQuery = val
      if (val && val.length > 1) {
        this.searchDebounce(item)
      } else {
        item.searchResults = []
      }
    },
    async doSearch(item) {
      if (
        !item.searchQuery ||
        item.searchQuery.length < 2 ||
        item.searchQuery === item.identifier
      ) {
        item.searchResults = []
        return
      }

      try {
        item.loading = true
        const response = await Contact.api()
          .filter({
            search: item.searchQuery
          })
          .all()
        const contacts = this._.get(response, 'response.data.data', [])
        item.searchResults = contacts.map(contact => {
          const value = contact.edrpou || contact.email
          return {
            display: contact.name || contact.email,
            value,
            email: contact?.email,
            edrpou: contact?.edrpou,
            name: contact?.name
          }
        })
      } catch (error) {
        item.searchResults = []
        this.$handlers.error(error, this)
      } finally {
        item.loading = false
      }
    },
    onFlipParticipantsEnd() {
      this.drag = false
      // updates indexes after flip
      for (const [idx, participant] of this.form.members.entries()) {
        participant.priority = idx + 1
      }
    },
    async submit() {
      const valid = await this.$refs.form.validate()
      if (!valid) {
        return
      }
      try {
        this.loading = true
        const payload = {
          ...this.form,
          strict:
            this.isEdit && this.route.strict === !this.canSkipApprovers
              ? this.route.strict
              : !this.canSkipApprovers, // todo: double check
          members: this.form.members.map(participant => ({
            identifier: participant.searchQuery,
            action: participant.action
          }))
        }
        if (this.isEdit) {
          await this.$store.dispatch('savedroutes/update', { id: this.route.id, payload })
        } else {
          await this.$store.dispatch('savedroutes/create', { payload })
        }
        await this.successSnackbar.open({
          text: this.isEdit ? this.$t('Route has been updated') : this.$t('Route has been created')
        })
        this.closeModal(true)
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.m-document-saved-participants-routes {
  &__content {
    padding: 40px 40px 24px 40px;
  }

  &__footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;

    padding: 32px 40px;
    border-top: 1px solid $border-disabled;
  }
}

.flip-list-move {
  transition: transform 0.5s;
}

.recipient {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  padding: 12px 12px 12px 42px;

  position: relative;

  border-radius: 12px;
  border: 2px dashed #e3e3e3;

  @media (min-width: map-get($breakpoints, 'sm')) {
    grid-template-columns: 1fr 1fr;
  }

  &--active {
    border: 2px dashed $primary;

    .recipient__dots {
      background: rgba(143, 37, 125, 0.16);
      border: 1px solid rgba(143, 37, 125, 0.16);
    }
  }

  &--disabled {
    padding: 12px;
  }

  &.deleteable {
    grid-template-columns: 1fr 1fr;
    padding-left: 42px;

    @media (min-width: map-get($breakpoints, 'sm')) {
      grid-template-columns: 1fr 1fr 48px;
    }
  }

  &__dots {
    width: 24px;
    height: 100%;
    background: #f5f6f7;
    border-radius: 12px 0 0 12px;
    border: 1px solid #f5f6f7;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: map-get($zIndexes, 'base');
  }

  &__email-field {
    grid-column: span 2;

    @media (min-width: map-get($breakpoints, 'sm')) {
      grid-column: 1;
    }
  }

  &__remove-btn {
    width: 100%;
  }
}

.recipient-combobox {
  ::v-deep .v-input__icon--append {
    display: none !important;
  }
}

.add-more-block {
  display: flex;
  justify-content: center;

  @media (min-width: map-get($breakpoints, 'sm')) {
    justify-content: flex-start;
  }
}
</style>
